import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Grid,
  Snackbar,
  Alert,
  Paper,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { API_URL } from "../../../Config/api";

const Closureeleven = ({
  evePlanID,
  onClose,
  emt,
  formNo,
  sessions,
  profET,
  profED,
  profST,
  profSD,
}) => {
  const accessToken = localStorage.getItem("token");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({
    startDate: false,
    startTime: false,
    endDate: false,
    endTime: false,
  });

  const [formData, setFormData] = useState({
    startDate: profSD || "",
    endDate: profED || "",
    startTime: profST || "",
    endTime: profET || "",
    impr_strgth_lip_jow: "",
    impr_strgth_lip_jow_rem: "",
    impr_intraoral_pressure: "",
    impr_intraoral_pressure_rem: "",
    speech_level: "",
    speech_level_rem: "",
    aac_usage: "",
    aac_usage_rem: "",
    speech_clarity: "",
    speech_clarity_rem: "",
    command_understanding: "",
    command_understanding_rem: "",
    word_recall: "",
    word_recall_rem: "",
    func_cognitive_skills: "",
    func_cognitive_skills_rem: "",
    func_reading_skills: "",
    func_reading_skills_rem: "",
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const newErrors = {
      startDate: !formData.startDate,
      startTime: !formData.startTime,
      endDate: !formData.endDate,
      endTime: !formData.endTime,
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some((isError) => isError)) {
      setSnackbarMessage("Please fill in all required fields.");
      setSeverity("error");
      setOpenSnackbar(true);
      return;
    }

    const start = new Date(`${formData.startDate}T${formData.startTime}`);
    const end = new Date(`${formData.endDate}T${formData.endTime}`);

    if (end <= start) {
      // Changed to English validation message
      setSnackbarMessage(
        "End date and time must be after the start date and time.",
      );
      setSeverity("error");
      setOpenSnackbar(true);
      setErrors((prev) => ({ ...prev, endDate: true, endTime: true }));
      return;
    }

    const requestData = {
      form_number: formNo,
      dtl_eve: evePlanID,
      prof_st_dt: formData.startDate,
      prof_ed_dt: formData.endDate,
      prof_st_time: formData.startTime,
      prof_ed_time: formData.endTime,
      impr_strgth_lip_jow: formData.impr_strgth_lip_jow === "Yes" ? 1 : 2,
      impr_strgth_lip_jow_remark: formData.impr_strgth_lip_jow_rem,
      impr_intraoral_pressure:
        formData.impr_intraoral_pressure === "Yes" ? 1 : 2,
      impr_intraoral_pressure_remark: formData.impr_intraoral_pressure_rem,
      speech_level: formData.speech_level === "Yes" ? 1 : 2,
      speech_level_remark: formData.speech_level_rem,
      aac_usage: formData.aac_usage === "Yes" ? 1 : 2,
      aac_usage_remark: formData.aac_usage_rem,
      speech_clarity: formData.speech_clarity === "Yes" ? 1 : 2,
      speech_clarity_remark: formData.speech_clarity_rem,
      command_understanding: formData.command_understanding === "Yes" ? 1 : 2,
      command_understanding_remark: formData.command_understanding_rem,
      word_recall: formData.word_recall === "Yes" ? 1 : 2,
      word_recall_remark: formData.word_recall_rem,
      func_cognitive_skills: formData.func_cognitive_skills === "Yes" ? 1 : 2,
      func_cognitive_skills_remark: formData.func_cognitive_skills_rem,
      func_reading_skills: formData.func_reading_skills === "Yes" ? 1 : 2,
      func_reading_skills_remark: formData.func_reading_skills_rem,
    };

    setLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/web/agg_hhc_session_job_closure/?dtl_eve=${evePlanID}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        },
      );
      const result = await response.json();

      if (response.ok) {
        setSnackbarMessage("Closure submitted successfully!");
        setSeverity("success");
        setOpenSnackbar(true);
        setTimeout(() => {
          onClose();
          emt();
        }, 1500);
      } else {
        setSnackbarMessage(result.msg || "Submission failed.");
        setSeverity("error");
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage("Network error!");
      setSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const speechQuestions = [
    {
      label: "1. Improve or motor strength for lip, tongue and jaw",
      field: "impr_strgth_lip_jow",
    },
    {
      label: "2. Improve intraoral air pressure",
      field: "impr_intraoral_pressure",
    },
    {
      label: "3. Speak at word, sentence and spontaneous speech level",
      field: "speech_level",
    },
    {
      label: "4. Use alternative augmentative communication for his/her needs",
      field: "aac_usage",
    },
    { label: "5. Able to speak clearly", field: "speech_clarity" },
    {
      label: "6. Improve understanding to follow commands",
      field: "command_understanding",
    },
    { label: "7. Recall words from specific category", field: "word_recall" },
    {
      label: "8. Develop functional, cognitive linguistic based skills",
      field: "func_cognitive_skills",
    },
    {
      label: "9. Develop functional reading skills",
      field: "func_reading_skills",
    },
  ];

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "80vh",
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          pb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", fontSize: "1.1rem" }}
        >
          CLOSURE FORM
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#666" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* SCROLLABLE CONTENT */}
      <Box sx={{ px: 3, py: 1, flex: 1, overflowY: "auto" }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <TextField
              label="Start Date *"
              fullWidth
              type="date"
              size="small"
              value={formData.startDate}
              InputLabelProps={{ shrink: true }}
              error={errors.startDate}
              helperText={errors.startDate && "Required"}
              onChange={(e) => {
                handleInputChange("startDate", e.target.value);
                setErrors((prev) => ({ ...prev, startDate: false }));
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Start Time *"
              fullWidth
              type="time"
              size="small"
              value={formData.startTime}
              InputLabelProps={{ shrink: true }}
              error={errors.startTime}
              helperText={errors.startTime && "Required"}
              onChange={(e) => {
                handleInputChange("startTime", e.target.value);
                setErrors((prev) => ({ ...prev, startTime: false }));
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="End Date *"
              fullWidth
              type="date"
              size="small"
              value={formData.endDate}
              InputLabelProps={{ shrink: true }}
              error={errors.endDate}
              helperText={errors.endDate && "Required"}
              onChange={(e) => {
                handleInputChange("endDate", e.target.value);
                setErrors((prev) => ({ ...prev, endDate: false }));
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="End Time *"
              fullWidth
              type="time"
              size="small"
              value={formData.endTime}
              InputLabelProps={{ shrink: true }}
              error={errors.endTime}
              helperText={errors.endTime && "Required"}
              onChange={(e) => {
                handleInputChange("endTime", e.target.value);
                setErrors((prev) => ({ ...prev, endTime: false }));
              }}
            />
          </Grid>
        </Grid>

        {speechQuestions.map((q, index) => (
          <Paper
            key={index}
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderRadius: "8px",
              borderColor: "#e0e0e0",
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body1"
              sx={{ color: "#333", mb: 1, fontWeight: 500 }}
            >
              {q.label}
            </Typography>
            <RadioGroup
              row
              value={formData[q.field]}
              onChange={(e) => handleInputChange(q.field, e.target.value)}
            >
              <FormControlLabel
                value="Yes"
                control={
                  <Radio
                    size="small"
                    sx={{ "&.Mui-checked": { color: "#666" } }}
                  />
                }
                label="Yes"
                sx={{ mr: 4 }}
              />
              <FormControlLabel
                value="No"
                control={
                  <Radio
                    size="small"
                    sx={{ "&.Mui-checked": { color: "#666" } }}
                  />
                }
                label="No"
              />
            </RadioGroup>
            {formData[q.field] === "No" && (
              <TextField
                fullWidth
                size="small"
                label="Remark for reason"
                variant="outlined"
                sx={{ mt: 2 }}
                onChange={(e) =>
                  handleInputChange(`${q.field}_rem`, e.target.value)
                }
              />
            )}
          </Paper>
        ))}
      </Box>

      {/* FIXED FOOTER */}
      <Box
        sx={{
          p: 2,
          backgroundColor: "#fff",
          borderTop: "1px solid #f0f0f0",
          display: "flex",
          justifyContent: "center",
          mt: "auto",
        }}
      >
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: "#2CE0AA",
            color: "#fff",
            fontWeight: "bold",
            px: 8,
            py: 1.2,
            borderRadius: "6px",
            fontSize: "15px",
            boxShadow: "none",
            "&:hover": { bgcolor: "#25c798", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#a6eed8", color: "#fff" },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "SUBMIT"}
        </Button>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert severity={severity} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Closureeleven;
